import React from "react";
import styles from "./FilterColumn.module.css";
import PropTypes from "prop-types";

function normalizeSelectedValues(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.keys(value).filter((key) => Boolean(value[key]));
  }

  return [];
}

function toIdPart(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function FilterColumn({ sections, selected, onChange, className }) {
  const isChecked = (section, option) => {
    const sectionKey = section.label;
    const selectedValues = normalizeSelectedValues(selected?.[sectionKey]);
    const selectedSet = new Set(selectedValues);

    return selectedSet.has(option.value);
  };

  const handleToggle = (section, option) => {
    const sectionKey = section.label;
    const selectedValues = normalizeSelectedValues(selected?.[sectionKey]);

    const nextSectionSelection = new Set(selectedValues);

    if (nextSectionSelection.has(option.value)) {
      nextSectionSelection.delete(option.value);
    } else {
      nextSectionSelection.add(option.value);
    }

    const nextSelected = {
      ...(selected || {}),
    };

    if (nextSectionSelection.size === 0) {
      delete nextSelected[sectionKey];
    } else {
      nextSelected[sectionKey] = Array.from(nextSectionSelection);
    }

    onChange(nextSelected);
  };

  return (
    <div className={`${styles.column} ${className || ""}`.trim()}>
      {(sections || []).map((section) => (
        <section className={styles.section} key={section.label}>
          <h3 className={styles.heading}>Filter by {section.label}</h3>
          <div className={styles.options}>
            {(section.options || []).map((option) => {
              const inputId = `filter-${toIdPart(section.label)}-${toIdPart(
                option.value,
              )}`;
              const checked = isChecked(section, option);

              return (
                <label
                  className={styles.optionRow}
                  key={option.value}
                  htmlFor={inputId}
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    className={styles.checkboxInput}
                    checked={checked}
                    onChange={() => handleToggle(section, option)}
                  />
                  <span className={styles.checkboxBox} aria-hidden="true">
                    <span className={styles.checkboxCross}>x</span>
                  </span>
                  <span className={styles.optionLabel}>{option.label}</span>
                </label>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

FilterColumn.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  selected: PropTypes.object,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

FilterColumn.defaultProps = {
  sections: [],
  selected: {},
  onChange: () => {},
  className: "",
};
